import React, { useMemo, useState, useEffect } from 'react';
import { useFinance } from '../context/FinanceContext';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Line, ReferenceLine, CartesianGrid
} from 'recharts';
import {
  ArrowUpRight, ArrowDownRight, AlertTriangle, CheckCircle2,
  Zap, AlertOctagon, ChevronRight, ChevronLeft, Calendar, Stethoscope, ArrowRight,
  Activity, Shield, TrendingUp, Sun, Moon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PortfolioTreemap, TreeItem } from '../components/PortfolioTreemap';

/* ════════════════════════════════════════════
   DESIGN TOKENS
   ════════════════════════════════════════════ */
const P_DARK = {
  bg:     '#f8fafc',
  s1:     '#0D1625',
  s2:     '#111E33',
  s3:     '#162340',
  border: '#1A2940',
  muted:  '#475569',
  subtle: '#94a3b8',
  text:   '#F8FAFC',
  gold:   '#C9A84C',
  goldLt: '#E8C96A',
  teal:   '#2DD4BF',
  tealDk: '#0F8A7C',
  rose:   '#F87171',
  sky:    '#38BDF8',
  violet: '#A78BFA',
  amber:  '#FBBF24',
  green:  '#34D399',
  c1:'#3B82F6', c2:'#2DD4BF', c3:'#C9A84C', c4:'#A78BFA', c5:'#F87171', c6:'#34D399', c7:'#FB923C',
};

const P_LIGHT = {
  bg:     '#f8fafc',
  s1:     '#ffffff',
  s2:     '#ffffff',
  s3:     '#f1f5f9',
  border: '#e2e8f0',
  muted:  '#64748b',
  subtle: '#94a3b8',
  text:   '#0f172a',
  gold:   '#C9A84C',
  goldLt: '#E8C96A',
  teal:   '#0d9488',
  tealDk: '#0f766e',
  rose:   '#e11d48',
  sky:    '#0284c7',
  violet: '#7c3aed',
  amber:  '#d97706',
  green:  '#16a34a',
  c1:'#2563eb', c2:'#0d9488', c3:'#b45309', c4:'#7c3aed', c5:'#e11d48', c6:'#16a34a', c7:'#ea580c',
};

const P = P_DARK;
const CC = [P.c1,P.c2,P.c3,P.c4,P.c5,P.c6,P.c7];

const getP = (isDarkMode: boolean) => isDarkMode ? P_DARK : P_LIGHT;

/* ════════════════════════════════════════════
   RESPONSIVE HOOK
   ════════════════════════════════════════════ */
function useIsMobile() {
  const [mob, setMob] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setMob(mq.matches);
    const handler = (e: MediaQueryListEvent) => setMob(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mob;
}

/* ════════════════════════════════════════════
   HELPERS
   ════════════════════════════════════════════ */
const fmt  = (n: number, d = 0) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtK = (n: number) => Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toFixed(0);

/* ════════════════════════════════════════════
   MICRO COMPONENTS
   ════════════════════════════════════════════ */
const DarkTip = ({ active, payload, label }: any) => {
  const { isDarkMode } = useFinance();
  const P = getP(isDarkMode);
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 10, padding: '9px 13px', fontSize: 12, color: P.text }}>
      {label && <div style={{ color: P.subtle, marginBottom: 5, fontSize: 11 }}>{label}</div>}
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ width: 7, height: 7, borderRadius: 2, background: p.fill || p.stroke || p.color, display: 'inline-block' }} />
          <span style={{ color: P.subtle }}>{p.name}:</span>
          <span style={{ fontWeight: 600 }}>{typeof p.value === 'number' ? fmt(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

function Card({ children, style = {}, onClick }: { children: React.ReactNode; style?: React.CSSProperties; onClick?: () => void }) {
  const [hov, setHov] = useState(false);
  const { isDarkMode } = useFinance();
  const P = getP(isDarkMode);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className="shadow-sm"
      style={{
        background: P.s2,
        border: `1px solid ${hov && onClick ? P.sky + '66' : P.border}`,
        borderRadius: 32,
        padding: 26,
        transition: 'all .25s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: hov && onClick ? 'translateY(-2px)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: isDarkMode ? 'none' : undefined,
        ...style,
      }}
    >{children}</div>
  );
}

function SectionLabel({ title, sub, action }: { title: string; sub?: string; action?: React.ReactNode }) {
  const { isDarkMode } = useFinance();
  const P = getP(isDarkMode);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
      <div>
        <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 600, color: P.text }}>{title}</div>
        {sub && <div style={{ fontSize: 10, color: P.muted, marginTop: 3, textTransform: 'uppercase', letterSpacing: '.09em' }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}

function NavBtn({ onClick }: { onClick: () => void }) {
  const [hov, setHov] = useState(false);
  const { isDarkMode } = useFinance();
  const P = getP(isDarkMode);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ background: 'none', border: `1px solid ${hov ? P.sky : P.border}`, borderRadius: 9, padding: '6px 9px', color: hov ? P.sky : P.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all .15s' }}
    ><ArrowRight size={15} /></button>
  );
}

function Pill({ positive, value, label }: { positive: boolean; value: string; label?: string }) {
  const { isDarkMode } = useFinance();
  const P = getP(isDarkMode);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 11, fontWeight: 600,
      color: positive ? P.teal : P.rose,
      background: positive ? 'rgba(45,212,191,.1)' : 'rgba(248,113,113,.1)',
      padding: '3px 8px', borderRadius: 99,
    }}>
      {positive ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
      {value}{label ? ` ${label}` : ''}
    </span>
  );
}

/* ════════════════════════════════════════════
   CHART 1 – RADIAL GAUGE (responsive)
   ════════════════════════════════════════════ */
function RadialGauge({ value, target = 30, max = 100, label, color, size = 170 }: {
  value: number; target?: number; max?: number; label: string; color: string; size?: number;
}) {
  const { isDarkMode } = useFinance();
  const P = getP(isDarkMode);
  const r = size * 0.38; const cx = size / 2; const cy = size * 0.52;
  const startAngle = 210; const sweepAngle = 240;
  const clamp = Math.min(Math.max(value, 0), max);
  const pct = clamp / max;
  const targetPct = target / max;

  const polarToXY = (angle: number, radius: number) => {
    const rad = ((angle - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };
  const arcPath = (from: number, to: number, ri: number, ro: number) => {
    const s = polarToXY(from, ro); const e = polarToXY(to, ro);
    const si = polarToXY(from, ri); const ei = polarToXY(to, ri);
    const large = (to - from) > 180 ? 1 : 0;
    return `M${s.x},${s.y} A${ro},${ro} 0 ${large} 1 ${e.x},${e.y} L${ei.x},${ei.y} A${ri},${ri} 0 ${large} 0 ${si.x},${si.y} Z`;
  };

  const toAngle = startAngle + sweepAngle * pct;
  const txy = polarToXY(startAngle + sweepAngle * targetPct, r + 5);
  const trackW = r * 0.16;

  return (
    <svg width={size} height={size * 0.92} viewBox={`0 0 ${size} ${size * 0.92}`} style={{ display: 'block', margin: '0 auto' }}>
      <path d={arcPath(startAngle, startAngle + sweepAngle, r - trackW, r)} fill={P.s3} />
      {pct > 0 && <path d={arcPath(startAngle, toAngle, r - trackW, r)} fill={color} opacity={0.9} />}
      <circle cx={txy.x} cy={txy.y} r={size * 0.022} fill={P.gold} />
      <text x={cx} y={cy - size * 0.05} textAnchor="middle" fill={P.text} fontSize={size * 0.155} fontWeight={700} fontFamily="'Sora',sans-serif">{value.toFixed(1)}%</text>
      <text x={cx} y={cy + size * 0.08} textAnchor="middle" fill={P.subtle} fontSize={size * 0.062} style={{ textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</text>
      <text x={cx} y={cy + size * 0.17} textAnchor="middle" fill={P.gold} fontSize={size * 0.055}>▲ {target}% target</text>
    </svg>
  );
}

/* ════════════════════════════════════════════
   CHART 2 – WATERFALL (responsive viewBox)
   ════════════════════════════════════════════ */
function WaterfallChart({ inflow, expenses, currency }: { inflow: number; expenses: { name: string; value: number }[]; currency: string }) {
  const { isDarkMode } = useFinance();
  const P = getP(isDarkMode);
  type WFBar = { name: string; base: number; value: number; type: 'start' | 'down' | 'net'; color: string; label: string };
  const symbol = useMemo(() => {
    if (currency === 'USD') return '$';
    if (currency === 'EUR') return '€';
    if (currency === 'AED') return 'AED';
    return currency;
  }, [currency]);

  const localFmtK = (n: number) => {
    const abs = Math.abs(n);
    const numPart = abs >= 1000 ? `${(abs / 1000).toFixed(1)}k` : abs.toFixed(0);
    return `${symbol}${numPart}`;
  };

  const bars: WFBar[] = useMemo(() => {
    const result: WFBar[] = [{ name: 'Income', base: 0, value: inflow, type: 'start', color: P.teal, label: `+${localFmtK(inflow)}` }];
    let running = inflow;
    expenses.slice(0, 5).forEach(e => {
      result.push({ name: e.name, base: running - e.value, value: e.value, type: 'down', color: P.rose, label: `-${localFmtK(e.value)}` });
      running -= e.value;
    });
    const net = running;
    result.push({ name: 'Net', base: 0, value: Math.max(net, 0), type: 'net', color: net >= 0 ? P.green : P.rose, label: net >= 0 ? `+${localFmtK(net)}` : localFmtK(net) });
    return result;
  }, [inflow, expenses, symbol, P]);

  const maxVal = Math.max(...bars.map(b => b.base + b.value), 1);
  const chartH = 120;
  const topPad = 22;
  const bottomPad = 24;
  const barW = 34;
  const gap = 12;
  const totalW = bars.length * (barW + gap) + 10;
  const totalH = chartH + topPad + bottomPad;

  return (
    <svg width="100%" viewBox={`0 0 ${totalW} ${totalH}`} preserveAspectRatio="xMidYMid meet">
      {bars.map((b, i) => {
        const x = 5 + i * (barW + gap);
        const barH = Math.max((b.value / maxVal) * chartH, 4);
        const barY = (b.type === 'down' ? chartH - (b.base + b.value) / maxVal * chartH : chartH - barH) + topPad;
        return (
          <g key={i}>
            {i > 0 && b.type !== 'net' && (() => {
              const prevTop = chartH - (bars[i - 1].base + bars[i - 1].value) / maxVal * chartH + topPad;
              return <line x1={x - gap} y1={prevTop} x2={x} y2={prevTop} stroke={P.border} strokeWidth={1} strokeDasharray="3 2" />;
            })()}
            <rect x={x} y={barY} width={barW} height={barH} rx={4} fill={b.color} opacity={b.type === 'net' ? 1 : 0.85} />
            <text x={x + barW / 2} y={barY - 5} textAnchor="middle" fill={b.color} fontSize={8} fontWeight={700}>{b.label}</text>
            <text x={x + barW / 2} y={chartH + topPad + 14} textAnchor="middle" fill={P.subtle} fontSize={8}>{b.name}</text>
          </g>
        );
      })}
      <line x1={0} y1={chartH + topPad} x2={totalW} y2={chartH + topPad} stroke={P.border} strokeWidth={1} />
    </svg>
  );
}

/* ════════════════════════════════════════════
   CHART 3 – MONTHLY SPEND HEATMAP
   Rows = years, Cols = Jan–Dec
   Cell colour encodes total spend that month
   ════════════════════════════════════════════ */
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function MonthHeatmap({ expenses, timeframe, periodBudget, periodExpenses }: { 
  expenses: any[]; 
  timeframe: string;
  periodBudget: number;
  periodExpenses: number;
}) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const { currency, convertToDisplay, isDarkMode, selectedYear, setSelectedYear, availableYears } = useFinance();
  const P = getP(isDarkMode);

  const monthIntensityColor = (v: number): string => {
    if (v === 0) return P.s3;
    if (isDarkMode) {
      if (v < 0.2) return '#101F35';
      if (v < 0.4) return '#162C4E';
      if (v < 0.6) return '#1C3A64';
      if (v < 0.8) return '#C9A84C';
      return P.rose;
    } else {
      if (v < 0.2) return '#cbd5e1'; // slate-300
      if (v < 0.4) return '#94a3b8'; // slate-400
      if (v < 0.6) return '#64748b'; // slate-500
      if (v < 0.8) return '#C9A84C'; // gold
      return P.rose;
    }
  };

  const { grid, years, maxSpend, peakKey } = useMemo(() => {
    const monthly: Record<string, number> = {};
    expenses.forEach(e => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      monthly[key] = (monthly[key] || 0) + convertToDisplay(e.amount);
    });
    const nowYear = new Date().getFullYear();
    const allYears = Array.from(new Set([
      ...Object.keys(monthly).map(k => parseInt(k.split('-')[0])),
      selectedYear,
      ...availableYears.slice(0, 4)
    ])).sort((a, b) => a - b);
    if (!allYears.length) allYears.push(nowYear);
    const maxSpend = Math.max(...Object.values(monthly), 1);
    const peakKey = Object.entries(monthly).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
    return { grid: monthly, years: allYears, maxSpend, peakKey };
  }, [expenses, convertToDisplay, selectedYear, availableYears]);

  const totalThisYear = useMemo(() => {
    const y = selectedYear;
    return MONTH_LABELS.reduce((sum, _, m) => sum + (grid[`${y}-${m}`] || 0), 0);
  }, [grid, selectedYear]);

  const overallBalance = convertToDisplay(periodBudget - periodExpenses);

  return (
    <div>
      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
        <div style={{ background: P.s3, borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 9, color: P.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 3 }}>
            Selected Year ({selectedYear})
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: P.text, fontFamily: "'Sora',sans-serif" }}>{currency} {fmt(totalThisYear)}</div>
        </div>
        <div style={{ background: P.s3, borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 9, color: P.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 3 }}>Overall Balance ({timeframe})</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: overallBalance >= 0 ? P.teal : P.rose, fontFamily: "'Sora',sans-serif" }}>
            {overallBalance >= 0 ? '+' : ''}{currency} {fmt(overallBalance)}
          </div>
        </div>
      </div>

      {/* Month column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '32px repeat(12, 1fr)', gap: 3, marginBottom: 3 }}>
        <div />
        {MONTH_LABELS.map(m => (
          <div key={m} style={{ textAlign: 'center', fontSize: 8, color: P.muted, textTransform: 'uppercase', letterSpacing: '.04em' }}>{m}</div>
        ))}
      </div>

      {/* Year rows */}
      {years.map(year => (
        <div key={year} style={{ display: 'grid', gridTemplateColumns: '32px repeat(12, 1fr)', gap: 3, marginBottom: 3 }}>
          <button
            onClick={() => setSelectedYear(year)}
            style={{ 
              fontSize: 9, 
              color: year === selectedYear ? (isDarkMode ? '#38BDF8' : '#2563EB') : P.muted, 
              fontWeight: year === selectedYear ? 800 : 500,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-end', 
              paddingRight: 4,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0
            }}
            title={`Select ${year}`}
          >
            {year}
          </button>
          {MONTH_LABELS.map((_, m) => {
            const key = `${year}-${m}`;
            const spend = grid[key] || 0;
            const intensity = spend / maxSpend;
            const bg = monthIntensityColor(intensity);
            const isHov = hoveredKey === key;
            const isFuture = new Date(year, m, 1) > new Date();
            return (
              <div
                key={m}
                onMouseEnter={() => !isFuture && setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(null)}
                style={{
                  height: 26, borderRadius: 5,
                  background: isFuture ? P.s1 : bg,
                  border: isHov ? `1.5px solid ${P.text}55` : `1px solid ${isFuture ? P.border + '44' : bg + '88'}`,
                  position: 'relative', cursor: isFuture ? 'default' : 'pointer',
                  opacity: isFuture ? 0.25 : 1,
                  transition: 'border-color .1s',
                }}
              >
                {isHov && spend > 0 && (
                  <div style={{
                    position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
                    background: P.s1, border: `1px solid ${P.border}`, borderRadius: 7,
                    padding: '5px 9px', fontSize: 11, color: P.text, whiteSpace: 'nowrap',
                    zIndex: 20, pointerEvents: 'none', fontWeight: 600,
                  }}>
                    {MONTH_LABELS[m]} {year}: {currency} {fmt(spend)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 10 }}>
        <span style={{ fontSize: 9, color: P.muted }}>No spend</span>
        {(isDarkMode 
          ? ['#101F35', '#162C4E', '#1C3A64', P.gold, P.rose]
          : ['#cbd5e1', '#94a3b8', '#64748b', P.gold, P.rose]
        ).map((c, i) => (
          <div key={i} style={{ width: 14, height: 14, borderRadius: 3, background: c }} />
        ))}
        <span style={{ fontSize: 9, color: P.muted }}>High</span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   CHART 4 – PORTFOLIO TREEMAP
   Imported from ../components/PortfolioTreemap
   ════════════════════════════════════════════ */

/* ════════════════════════════════════════════
   CHART 5 – BULLET CHART
   ════════════════════════════════════════════ */
function BulletChart({ items }: { items: { name: string; spent: number; budget: number }[] }) {
  const { currency, isDarkMode } = useFinance();
  const P = getP(isDarkMode);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      {items.map((item, i) => {
        const pct = Math.min((item.spent / Math.max(item.budget, 1)) * 105, 120);
        const over = item.spent > item.budget;
        const color = over ? P.rose : pct > 75 ? P.amber : P.teal;
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: P.subtle }}>{item.name}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: over ? P.rose : P.text }}>
                {currency} {fmtK(item.spent)} <span style={{ color: P.muted, fontWeight: 400 }}>/ {currency} {fmtK(item.budget)}</span>
              </span>
            </div>
            <div style={{ height: 8, background: P.s3, borderRadius: 99, position: 'relative', overflow: 'visible' }}>
              <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 99, transition: 'width 1s ease', position: 'relative' }}>
                {over && <div style={{ position: 'absolute', right: -1, top: -2, width: 12, height: 12, borderRadius: 99, background: P.rose, border: `2px solid ${P.s2}` }} />}
              </div>
              <div style={{ position: 'absolute', left: '100%', top: -3, transform: 'translateX(-1px)', width: 2, height: 14, background: P.gold, borderRadius: 1 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN DASHBOARD
   ════════════════════════════════════════════ */
const getEventOccurrencesInPeriod = (event: any, periodMonths: Date[]) => {
  let count = 0;
  const eventStart = new Date(event.startDate);
  const eventEnd = event.endDate ? new Date(event.endDate) : null;

  periodMonths.forEach(monthStart => {
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);

    if (eventStart > monthEnd) return;
    if (eventEnd && eventEnd < monthStart) return;

    if (event.frequency === 'Once') {
      if (eventStart >= monthStart && eventStart <= monthEnd) {
        count++;
      }
    } else if (event.frequency === 'Monthly') {
      count++;
    } else if (event.frequency === 'Quarterly') {
      const diffMonths = (monthStart.getFullYear() - eventStart.getFullYear()) * 12 + (monthStart.getMonth() - eventStart.getMonth());
      if (diffMonths >= 0 && diffMonths % 3 === 0) {
        count++;
      }
    } else if (event.frequency === 'Yearly') {
      if (monthStart.getMonth() === eventStart.getMonth()) {
        count++;
      }
    }
  });

  return count;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { 
    expenses, budgets, investments, investmentCategories, balanceSheet, cashFlowEvents, 
    currency, convertToDisplay, janBaseline, isDarkMode, setIsDarkMode,
    passiveIncomeGoal, passiveIncomeStats,
    lifestyleMonthlyExpenses, setLifestyleMonthlyExpenses, financialFreedomStats,
    selectedYear, setSelectedYear, availableYears
  } = useFinance();
  const P = getP(isDarkMode);
  const CC = [P.c1, P.c2, P.c3, P.c4, P.c5, P.c6, P.c7];
  const [timeframe, setTimeframe] = useState<'MTD' | 'QTD' | 'YTD'>('YTD');

  useEffect(() => {
    if (!document.getElementById('dash-fonts')) {
      const l = document.createElement('link');
      l.id = 'dash-fonts'; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&family=DM+Sans:wght@400;500&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  /* ── TIME FRAME DATE CALCS ── */
  const periodDates = useMemo(() => {
    const today = new Date();
    const currentSystemYear = today.getFullYear();
    const currentSystemMonth = today.getMonth();
    const year = selectedYear;

    let startMonth = 0;
    let endMonth = 11;

    if (timeframe === 'MTD') {
      const m = selectedYear === currentSystemYear ? currentSystemMonth : 0;
      startMonth = m;
      endMonth = m;
    } else if (timeframe === 'QTD') {
      const m = selectedYear === currentSystemYear ? currentSystemMonth : 0;
      startMonth = Math.floor(m / 3) * 3;
      endMonth = startMonth + 2;
    } else if (timeframe === 'YTD') {
      startMonth = 0;
      endMonth = 11;
    }

    const startD = new Date(year, startMonth, 1);
    const endD = new Date(year, endMonth + 1, 0); // last day of endMonth

    // Safe string formatting
    const startStr = `${startD.getFullYear()}-${String(startD.getMonth() + 1).padStart(2, '0')}-01`;
    const endStr = `${endD.getFullYear()}-${String(endD.getMonth() + 1).padStart(2, '0')}-${String(endD.getDate()).padStart(2, '0')}`;

    // List of elapsed or projected month-starts in the selected period
    const maxMonthToInclude = (selectedYear === currentSystemYear && timeframe === 'YTD') ? currentSystemMonth : endMonth;
    const elapsedMonths: Date[] = [];
    for (let m = startMonth; m <= maxMonthToInclude; m++) {
      elapsedMonths.push(new Date(year, m, 1));
    }
    if (elapsedMonths.length === 0) {
      elapsedMonths.push(new Date(year, startMonth, 1));
    }

    return { 
      startDateStr: startStr, 
      endDateStr: endStr, 
      elapsedMonths,
    };
  }, [timeframe, selectedYear]);

  /* ── CORE CALCS ── */
  const fd = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const targetDate = selectedYear === today.getFullYear()
      ? today
      : new Date(selectedYear, 11, 31, 23, 59, 59);

    const activeLedger = balanceSheet.filter(item => {
      const start = new Date(item.startDate);
      start.setHours(0, 0, 0, 0);
      const end = item.endDate ? new Date(item.endDate) : new Date('2099-12-31');
      end.setHours(23, 59, 59, 999);
      return targetDate >= start && targetDate <= end;
    });

    const investVal = investmentCategories.reduce((a, c) => a + c.currentValue, 0);
    const manualAssets = activeLedger.filter(i => i.type === 'Asset').reduce((a, c) => a + c.value, 0);
    const liabilities = activeLedger.filter(i => i.type === 'Liability').reduce((a, c) => a + c.value, 0);
    const totalAssets = manualAssets + investVal;
    
    // 1. Net worth should be calculated as all assets minus all liabilities
    const netWorth = totalAssets - liabilities;

    // Calculate occurrences of recurring inflow/outflow events in the selected elapsed months
    let inflow = 0;
    let outflowEventsSum = 0;
    cashFlowEvents.forEach(e => {
      const occurrences = getEventOccurrencesInPeriod(e, periodDates.elapsedMonths);
      if (occurrences > 0) {
        if (e.type === 'Inflow') {
          inflow += e.amount * occurrences;
        } else if (e.type === 'Outflow') {
          outflowEventsSum += e.amount * occurrences;
        }
      }
    });

    // Individual expenses in the selected period
    const curExpenses = expenses.filter(e => e.date >= periodDates.startDateStr && e.date <= periodDates.endDateStr);
    const expSum = curExpenses.reduce((a, c) => a + c.amount, 0);

    // 2. Cash flow should be all inflow on the related timeperiod minus all the outflow of the selected period
    // Outflow is the sum of logged individual expenses plus recurring outflow events
    const totalOutflow = expSum + outflowEventsSum;
    const cashFlow = inflow - totalOutflow;

    const budgetTotalSingleMonth = budgets.reduce((a, c) => a + c.budget, 0);
    const budgetTotal = budgetTotalSingleMonth * periodDates.elapsedMonths.length;

    // 3. Saving rate should be 100% minus all the expenses of the selected period divided by the total budget of the selected period
    const savingsRate = budgetTotal > 0 ? 100 - (expSum / budgetTotal) * 100 : 0;

    const totalInvested = investments.reduce((a, c) => a + c.investedAmount, 0);
    const unrealizedGain = investVal - totalInvested;
    const roi = totalInvested > 0 ? (unrealizedGain / totalInvested) * 100 : 0;
    const monthlyOut = cashFlowEvents.filter(e => e.type === 'Outflow' && e.frequency === 'Monthly').reduce((a, c) => a + c.amount, 0) + budgetTotalSingleMonth;
    const runway = monthlyOut > 0 ? manualAssets / monthlyOut : 12;

    const overBudget = budgets.map(b => ({
      name: b.category,
      spent: curExpenses.filter(e => e.category === b.category).reduce((s, e) => s + e.amount, 0),
      budget: b.budget * periodDates.elapsedMonths.length,
    })).filter(x => x.spent > x.budget);

    const debtRatio = totalAssets > 0 ? (liabilities / totalAssets) * 100 : 0;

    return { netWorth, totalAssets, liabilities, inflow, expSum, totalOutflow, cashFlow, budgetTotal: budgetTotalSingleMonth, periodBudgetTotal: budgetTotal, savingsRate, investVal, unrealizedGain, roi, totalInvested, manualAssets, monthlyOut, runway, overBudget, debtRatio };
  }, [expenses, investments, investmentCategories, balanceSheet, cashFlowEvents, budgets, periodDates, selectedYear]);

  /* ── YEAR-OVER-YEAR COMPARISON ── */
  const yoyStats = useMemo(() => {
    const prevYear = selectedYear - 1;
    const prevYearPrefix = `${prevYear}-`;
    const curYearPrefix = `${selectedYear}-`;

    const prevExp = expenses
      .filter(e => e.date.startsWith(prevYearPrefix))
      .reduce((s, e) => s + e.amount, 0);
    const curExp = expenses
      .filter(e => e.date.startsWith(curYearPrefix))
      .reduce((s, e) => s + e.amount, 0);

    const expenseDelta = prevExp > 0 ? ((curExp - prevExp) / prevExp) * 100 : null;

    let prevInflow = 0;
    let curInflow = 0;
    for (let m = 0; m < 12; m++) {
      const prevMStart = new Date(prevYear, m, 1);
      const prevMEnd = new Date(prevYear, m + 1, 0);
      const curMStart = new Date(selectedYear, m, 1);
      const curMEnd = new Date(selectedYear, m + 1, 0);

      cashFlowEvents.forEach(e => {
        if (e.type === 'Inflow') {
          const st = new Date(e.startDate);
          const en = e.endDate ? new Date(e.endDate) : new Date('2099-12-31');
          if (st <= prevMEnd && en >= prevMStart) prevInflow += e.amount;
          if (st <= curMEnd && en >= curMStart) curInflow += e.amount;
        }
      });
    }

    const incomeDelta = prevInflow > 0 ? ((curInflow - prevInflow) / prevInflow) * 100 : null;

    return {
      prevYear,
      prevExp,
      curExp,
      expenseDelta,
      prevInflow,
      curInflow,
      incomeDelta
    };
  }, [expenses, cashFlowEvents, selectedYear]);

  const nwGrowth = useMemo(() => {
    if (!janBaseline?.netWorth) return null;
    return ((fd.netWorth - janBaseline.netWorth) / Math.abs(janBaseline.netWorth)) * 100;
  }, [fd.netWorth, janBaseline]);

  /* ── CHART DATA ── */
  const netWorthTrend = useMemo(() => {
    const now = new Date();
    const base = janBaseline?.netWorth ?? fd.netWorth * 0.88;
    const count = isMobile ? 5 : 8;
    return Array.from({ length: count }, (_: unknown, i: number) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (count - 1) + i, 1);
      const label = d.toLocaleString('default', { month: 'short' });
      const growth = base + (fd.netWorth - base) * (i / (count - 1)) * (1 + (Math.random() - 0.5) * 0.1);
      return { label, v: convertToDisplay(Math.round(growth)) };
    });
  }, [fd.netWorth, janBaseline, convertToDisplay, isMobile]);

  const expensesByCategory = useMemo(() => {
    return budgets.map(b => {
      const spentRaw = expenses
        .filter(e => e.category === b.category && e.date >= periodDates.startDateStr && e.date <= periodDates.endDateStr)
        .reduce((s, e) => s + e.amount, 0);
      return {
        name: b.category,
        spent: convertToDisplay(spentRaw),
        budget: convertToDisplay(b.budget * periodDates.elapsedMonths.length),
      };
    }).filter(b => b.budget > 0).sort((a, b) => b.spent - a.spent);
  }, [expenses, budgets, convertToDisplay, periodDates]);

  const monthlyExpenseTrend = useMemo(() => {
    const now = new Date();
    const count = isMobile ? 4 : 6;
    const isCurrent = selectedYear === now.getFullYear();

    return Array.from({ length: count }, (_, i) => {
      const d = isCurrent 
        ? new Date(selectedYear, now.getMonth() - (count - 1) + i, 1)
        : new Date(selectedYear, i, 1);
      const mStr = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
      const label = d.toLocaleString('default', { month: 'short' });
      const point: any = { label, budget: convertToDisplay(fd.budgetTotal) };
      budgets.forEach(b => {
        point[b.category] = convertToDisplay(expenses.filter(e => e.category === b.category && e.date.startsWith(mStr)).reduce((s, e) => s + e.amount, 0));
      });
      return point;
    });
  }, [expenses, budgets, fd.budgetTotal, convertToDisplay, isMobile, selectedYear]);

  const allocationData = useMemo(() => {
    const totalPortfolioVal = investmentCategories.reduce((acc, curr) => acc + curr.currentValue, 0);

    return investmentCategories.map((c, i) => {
      const totalAmountDisp = convertToDisplay(c.currentValue);

      // Delta of the category value on the selected timeframe
      const sortedHistory = [...c.history].sort((a, b) => a.date.localeCompare(b.date));

      const endingPoint = sortedHistory
        .filter(h => h.date && h.date <= periodDates.endDateStr)
        .pop();
      const endingValRaw = endingPoint ? endingPoint.value : c.currentValue;

      const startingPoint = sortedHistory
        .filter(h => h.date && h.date <= periodDates.startDateStr)
        .pop();
      const startingValRaw = startingPoint 
        ? startingPoint.value 
        : (sortedHistory.length > 0 ? sortedHistory[0].value : 0);

      const deltaValueDisp = convertToDisplay(endingValRaw - startingValRaw);

      // ROI of the category (lifetime Return on Investment)
      const lifetimeInvested = investments
        .filter(inv => inv.category === c.name)
        .reduce((sum, inv) => sum + inv.investedAmount, 0);
      const roi = lifetimeInvested > 0
        ? ((c.currentValue - lifetimeInvested) / lifetimeInvested) * 100
        : 0;

      // % of total category compared to full portfolio
      const percentOfPortfolio = totalPortfolioVal > 0
        ? (c.currentValue / totalPortfolioVal) * 100
        : 0;

      return {
        name: c.name,
        value: totalAmountDisp,
        totalRaw: totalAmountDisp,
        timeframeValue: deltaValueDisp,
        roi,
        percentOfPortfolio,
        color: CC[i % CC.length]
      };
    }).filter(d => d.value > 0);
  }, [investmentCategories, investments, periodDates, convertToDisplay, CC]);

  const waterfallExpenses = useMemo(() => {
    // 1. Group active expenses (for selected timeframe) by category
    const categoryTotals: Record<string, number> = {};
    expenses.forEach(e => {
      if (e.date >= periodDates.startDateStr && e.date <= periodDates.endDateStr) {
        categoryTotals[e.category] = (categoryTotals[e.category] || 0) + e.amount;
      }
    });

    // 2. Convert to displayed format & sort
    const list = Object.entries(categoryTotals)
      .map(([category, rawAmount]) => ({
        name: category,
        value: convertToDisplay(rawAmount),
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);

    // 3. Make sure to club the lower spend categories into 'Other' if we have more than 5 categories
    if (list.length <= 5) {
      return list.map(item => ({
        name: item.name.slice(0, isMobile ? 6 : 10),
        value: item.value,
      }));
    } else {
      // Keep top 4
      const top4 = list.slice(0, 4);
      // Sum the rest as 'Other'
      const restSum = list.slice(4).reduce((sum, item) => sum + item.value, 0);
      
      const result = top4.map(item => ({
        name: item.name.slice(0, isMobile ? 6 : 10),
        value: item.value,
      }));
      
      if (restSum > 0) {
        result.push({
          name: 'Other',
          value: restSum,
        });
      }
      return result;
    }
  }, [expenses, convertToDisplay, isMobile, periodDates]);

  const waterfallInflow = useMemo(() => {
    // 1. Seek direct salary/income events in user's recurring list
    const salaryEvent = cashFlowEvents.find(e => 
      e.type === 'Inflow' && 
      e.description.toLowerCase().includes('salary')
    ) || cashFlowEvents.find(e => 
      e.type === 'Inflow' && 
      (e.description.toLowerCase().includes('income') || e.description.toLowerCase().includes('wage') || e.description.toLowerCase().includes('pay'))
    ) || cashFlowEvents.find(e => e.type === 'Inflow');

    const numMonths = Math.max(periodDates.elapsedMonths.length, 1);

    if (salaryEvent) {
      return convertToDisplay(salaryEvent.amount) * numMonths;
    }

    // 2. If no event explicitly matches, try to use aggregate inflows or default placeholder
    const sumMonthlyInflowsRaw = cashFlowEvents
      .filter(e => e.type === 'Inflow')
      .reduce((sum, e) => sum + e.amount, 0);

    if (sumMonthlyInflowsRaw > 0) {
      return convertToDisplay(sumMonthlyInflowsRaw) * numMonths;
    }

    // Default fallback to 5000 displays per month if budget profile is empty
    return 5000 * numMonths;
  }, [cashFlowEvents, periodDates.elapsedMonths.length, convertToDisplay]);

  /* ── INSIGHTS ── */
  const insights = useMemo(() => {
    const list: { icon: React.ReactNode; title: string; body: string; level: 'high' | 'medium' | 'ok' }[] = [];
    if (fd.expSum > fd.inflow && fd.inflow > 0) list.push({ icon: <AlertOctagon size={14} />, title: 'Capital erosion', body: `Burn rate exceeds income by ${(((fd.expSum - fd.inflow) / fd.inflow) * 100).toFixed(0)}%.`, level: 'high' });
    if (fd.runway < 3) list.push({ icon: <AlertTriangle size={14} />, title: 'Critical runway', body: `Only ${fd.runway.toFixed(1)} months cash reserves.`, level: 'high' });
    if (fd.overBudget.length > 0) list.push({ icon: <Zap size={14} />, title: 'Budget leakage', body: `${fd.overBudget.map(x => x.name).join(', ')} over limit.`, level: 'medium' });
    investmentCategories.forEach(c => { const w = (c.currentValue / Math.max(fd.investVal, 1)) * 100; if (w > 50) list.push({ icon: <Activity size={14} />, title: 'Concentration risk', body: `"${c.name}" is ${w.toFixed(0)}% of portfolio.`, level: 'medium' }); });
    if (fd.roi > 7) list.push({ icon: <TrendingUp size={14} />, title: 'Alpha achieved', body: `ROI ${fd.roi.toFixed(1)}% beats 7% benchmark.`, level: 'ok' });
    if (fd.runway >= 6) list.push({ icon: <Shield size={14} />, title: 'Liquidity strong', body: `${fd.runway.toFixed(0)}-month cash runway secured.`, level: 'ok' });
    if (fd.savingsRate >= 30) list.push({ icon: <CheckCircle2 size={14} />, title: 'Elite savings rate', body: `${fd.savingsRate.toFixed(1)}% — well above 30% target.`, level: 'ok' });
    return list;
  }, [fd, investmentCategories]);

  const levelStyle: Record<string, { bg: string; dot: string; border: string }> = {
    high:   { bg: 'rgba(248,113,113,.07)', dot: P.rose,  border: P.rose  + '33' },
    medium: { bg: 'rgba(251,191,36,.07)',  dot: P.amber, border: P.amber + '33' },
    ok:     { bg: 'rgba(45,212,191,.07)',  dot: P.teal,  border: P.teal  + '33' },
  };

  const now = new Date();
  const disp = (n: number) => convertToDisplay(n);

  // Responsive grid helpers
  const col2 = isMobile ? '1fr' : '1fr 1fr';
  const col3 = isMobile ? '1fr' : 'repeat(3, 1fr)';
  const col4 = isMobile ? '1fr 1fr' : 'repeat(4,1fr)';
  const col31 = isMobile ? '1fr' : '1fr 320px';
  const gap = isMobile ? 12 : 16;

  /* ════════════ RENDER ════════════ */
  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", paddingBottom: 60 }}>

      {/* ── HEADER ── */}
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 sm:gap-8 mb-6" style={{ fontFamily: "'DM Sans',sans-serif" }}>
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tighter leading-none" style={{ fontFamily: "'Sora',sans-serif" }}>Dashboard</h1>
          <p className="text-slate-400 font-bold text-xs sm:text-sm uppercase tracking-widest mt-2">
            {now.toLocaleDateString('en-US', { weekday: isMobile ? undefined : 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', background: P.s2, border: `1px solid ${P.border}`, borderRadius: 11, padding: 3, gap: 2 }}>
            {(['MTD', 'QTD', 'YTD'] as const).map(tf => (
              <button key={tf} onClick={() => setTimeframe(tf)} style={{ padding: isMobile ? '5px 12px' : '6px 17px', fontSize: 11, fontWeight: 600, letterSpacing: '.05em', borderRadius: 8, border: 'none', cursor: 'pointer', transition: 'all .15s', background: timeframe === tf ? P.sky : 'transparent', color: timeframe === tf ? P.s1 : P.muted, fontFamily: "'DM Sans',sans-serif" }}>{tf}</button>
            ))}
          </div>
        </div>
      </header>

      {/* Year-over-Year Perspective Banner */}
      <div className={`p-4 rounded-2xl mb-6 flex flex-wrap items-center justify-between gap-4 border transition-all ${
        isDarkMode ? 'bg-[#111E33] border-[#1A2940]' : 'bg-gradient-to-r from-blue-50/70 via-indigo-50/40 to-slate-50 border-blue-100'
      }`}>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-600 text-white shadow-xs">
            <Calendar size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-black text-slate-900 dark:text-white font-['Sora',sans-serif]">
                Year-over-Year Outlook ({selectedYear})
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                {selectedYear} vs {yoyStats.prevYear}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              Data, projections, and monthly performance dynamically synced for {selectedYear}.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-5 sm:gap-7 text-xs">
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-400">Total Outflow YoY</div>
            <div className="font-['Sora',sans-serif] font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
              <span>{currency} {fmt(disp(fd.totalOutflow))}</span>
              {yoyStats.expenseDelta !== null && (
                <span className={`text-[10px] font-bold ${yoyStats.expenseDelta <= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  ({yoyStats.expenseDelta > 0 ? '+' : ''}{yoyStats.expenseDelta.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-400">Forecast Inflow YoY</div>
            <div className="font-['Sora',sans-serif] font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
              <span>{currency} {fmt(disp(fd.inflow))}</span>
              {yoyStats.incomeDelta !== null && (
                <span className={`text-[10px] font-bold ${yoyStats.incomeDelta >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  ({yoyStats.incomeDelta > 0 ? '+' : ''}{yoyStats.incomeDelta.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-400">Period Net Balance</div>
            <div className={`font-['Sora',sans-serif] font-bold ${fd.cashFlow >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
              {fd.cashFlow >= 0 ? '+' : ''}{currency} {fmt(disp(fd.cashFlow))}
            </div>
          </div>
        </div>
      </div>

      {/* gold rule */}
      <div style={{ height: 1, background: `linear-gradient(90deg,${P.gold}55,${P.gold}22,transparent)`, marginBottom: 22 }} />

      {/* ══ ROW 1 — KPI CARDS (Normalized Typography, Baseline Alignment & Clean 3-Metric View) ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: col3, gap, marginBottom: gap }}>
        {/* Net Worth */}
        <Card onClick={() => navigate('/balance-sheet')}>
          <div style={{ height: 18, display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', color: P.muted, fontWeight: 700 }}>Net Worth</span>
          </div>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: isMobile ? 22 : 26, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, color: isDarkMode ? '#ffffff' : P.text, marginBottom: 6, height: isMobile ? 26 : 30, display: 'flex', alignItems: 'center' }}>
            {currency} {fmt(disp(fd.netWorth))}
          </div>
          <div style={{ fontSize: 10, color: P.subtle, marginBottom: 10, height: 16, display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currency} {fmt(disp(fd.totalAssets))} assets · {currency} {fmt(disp(fd.liabilities))} debt
          </div>
          <div style={{ height: 5, background: P.s3, borderRadius: 99, overflow: 'hidden' }}>
            <div 
              style={{ 
                height: '100%', 
                width: `${Math.min(Math.max((fd.netWorth / Math.max(fd.totalAssets, 1)) * 100, 0), 100)}%`, 
                background: P.teal, 
                borderRadius: 99, 
                transition: 'width 0.8s ease' 
              }} 
            />
          </div>
        </Card>

        {/* Cash Flow */}
        <Card onClick={() => navigate('/expenses')}>
          <div style={{ height: 18, display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', color: P.muted, fontWeight: 700 }}>Cash Flow</span>
          </div>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: isMobile ? 22 : 26, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, color: fd.cashFlow >= 0 ? P.teal : P.rose, marginBottom: 6, height: isMobile ? 26 : 30, display: 'flex', alignItems: 'center' }}>
            {fd.cashFlow >= 0 ? '+' : ''}{currency} {fmt(disp(fd.cashFlow))}
          </div>
          <div style={{ fontSize: 10, color: P.subtle, marginBottom: 10, height: 16, display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currency} {fmt(disp(fd.inflow))} in · {currency} {fmt(disp(fd.expSum))} out
          </div>
          <div style={{ height: 5, background: P.s3, borderRadius: 99, overflow: 'hidden' }}>
            <div 
              style={{ 
                height: '100%', 
                width: `${Math.min((fd.expSum / Math.max(fd.inflow, 1)) * 100, 100)}%`, 
                background: fd.cashFlow >= 0 ? P.teal : P.rose, 
                borderRadius: 99, 
                transition: 'width 0.8s ease' 
              }} 
            />
          </div>
        </Card>

        {/* Financial Freedom Indicator (Pure Visualization) */}
        <Card onClick={() => navigate('/investments')}>
          <div style={{ height: 18, display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', color: P.muted, fontWeight: 700 }}>Financial Freedom</span>
          </div>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: isMobile ? 22 : 26, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, color: P.teal, marginBottom: 6, height: isMobile ? 26 : 30, display: 'flex', alignItems: 'center' }}>
            {financialFreedomStats.freedomPercentage.toFixed(1)}%
          </div>
          <div style={{ fontSize: 10, color: P.subtle, marginBottom: 10, height: 16, display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {financialFreedomStats.isFreedomAchieved ? (
              <span style={{ color: P.teal, fontWeight: 600 }}>100% lifestyle covered</span>
            ) : (
              <span>
                Remaining to invest: <strong style={{ color: isDarkMode ? P.goldLt : P.gold, fontWeight: 700 }}>{currency} {fmt(disp(financialFreedomStats.capitalNeededToInvest))}</strong>
              </span>
            )}
          </div>
          <div style={{ height: 5, background: P.s3, borderRadius: 99, overflow: 'hidden' }}>
            <div 
              style={{ 
                height: '100%', 
                width: `${Math.min(financialFreedomStats.freedomPercentage, 100)}%`, 
                background: P.teal, 
                borderRadius: 99, 
                transition: 'width 0.8s ease' 
              }} 
            />
          </div>
        </Card>
      </div>

      {/* ══ ROW 2 — Waterfall + Budget vs Actual ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: col2, gap, marginBottom: gap }}>
        <Card>
          <SectionLabel title="Cash Flow Waterfall" sub={timeframe === 'MTD' ? `${now.toLocaleString('default', { month: 'long' })} breakdown` : `${timeframe} breakdown`} />
          <WaterfallChart inflow={waterfallInflow} expenses={waterfallExpenses} currency={currency} />
        </Card>
        <Card>
          <SectionLabel title="Budget vs Actual" sub="Current month · bullet view" action={<NavBtn onClick={() => navigate('/expenses')} />} />
          <BulletChart items={expensesByCategory.slice(0, isMobile ? 5 : 7)} />
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${P.border}` }}>
            {[{ c: P.teal, l: 'On track' }, { c: P.amber, l: '>75%' }, { c: P.rose, l: 'Over budget' }, { c: P.gold, l: 'Budget cap' }].map((x, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: i === 3 ? 2 : 8, height: i === 3 ? 12 : 8, borderRadius: i === 3 ? 1 : 2, background: x.c }} />
                <span style={{ fontSize: 10, color: P.muted }}>{x.l}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ══ ROW 3 — Spend Heatmap + 6-Month Expense Trend ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: col2, gap, marginBottom: gap }}>
        <Card>
          <SectionLabel title="Spend Heatmap" sub="Monthly intensity by year" />
          <MonthHeatmap expenses={expenses} timeframe={timeframe} periodBudget={fd.periodBudgetTotal} periodExpenses={fd.expSum} />
        </Card>
        <Card>
          <SectionLabel title={isMobile ? 'Expense Trend' : '6-Month Expense Trend'} sub="Stacked by category" action={<NavBtn onClick={() => navigate('/expenses')} />} />
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyExpenseTrend} margin={{ top: 4, right: 4, left: isMobile ? -28 : -18, bottom: 0 }}>
                <defs>
                  {budgets.slice(0, 6).map((b, i) => (
                    <linearGradient key={b.category} id={`g${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CC[i]} stopOpacity={0.7} />
                      <stop offset="100%" stopColor={CC[i]} stopOpacity={0.15} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={P.border} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: P.muted }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: P.muted }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<DarkTip />} cursor={{ stroke: P.border, strokeWidth: 1 }} />
                {budgets.slice(0, 6).map((b, i) => (
                  <Area key={b.category} type="monotone" dataKey={b.category} stackId="1" stroke={CC[i]} strokeWidth={1.5} fill={`url(#g${i})`} />
                ))}
                <Line type="monotone" dataKey="budget" name="Budget" stroke={P.gold} strokeWidth={2} strokeDasharray="5 4" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* ══ ROW 4 — Treemap + Balance sheet ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: col2, gap, marginBottom: gap }}>
        <Card>
          <SectionLabel title="Portfolio Treemap" sub="Area = allocation weight" action={<NavBtn onClick={() => navigate('/investments')} />} />
          <PortfolioTreemap data={allocationData} roi={fd.roi} positive={fd.unrealizedGain >= 0} currency={currency} timeframe={timeframe} roiValue={convertToDisplay(fd.unrealizedGain)} portfolioValue={convertToDisplay(fd.investVal)} />
        </Card>
        <Card>
          <SectionLabel title="Balance Sheet" sub="Assets · liabilities · solvency & capital structure" action={<NavBtn onClick={() => navigate('/balance-sheet')} />} />
          
          {/* Net Worth Banner */}
          <div style={{ background: `linear-gradient(135deg,${P.gold}18,${P.gold}08)`, border: `1px solid ${P.gold}33`, borderRadius: 16, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 10, color: P.gold, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 3, fontWeight: 700 }}>Net Worth</div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: isMobile ? 22 : 25, fontWeight: 800, color: isDarkMode ? '#ffffff' : P.text }}>{currency} {fmt(disp(fd.netWorth))}</div>
            </div>
            {nwGrowth !== null && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9.5, color: P.muted, marginBottom: 3 }}>Since Jan Baseline</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: nwGrowth >= 0 ? P.teal : P.rose, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                  {nwGrowth >= 0 ? <ArrowUpRight size={15} /> : <ArrowDownRight size={15} />}
                  {Math.abs(nwGrowth).toFixed(1)}%
                </div>
              </div>
            )}
          </div>

          {/* Asset vs Liability Proportion Bar */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: P.muted, marginBottom: 5 }}>
              <span style={{ fontWeight: 600 }}>Assets {currency} {fmt(disp(fd.totalAssets))}</span>
              <span style={{ fontWeight: 600 }}>Liabilities {currency} {fmt(disp(fd.liabilities))}</span>
            </div>
            <div style={{ height: 8, display: 'flex', borderRadius: 99, overflow: 'hidden', gap: 2 }}>
              <div style={{ flex: Math.max(fd.totalAssets, 1), background: P.teal, borderRadius: '99px 0 0 99px' }} />
              <div style={{ flex: Math.max(fd.liabilities, 0.01), background: P.rose, borderRadius: '0 99px 99px 0' }} />
            </div>
          </div>

          {/* Primary Balance Sheet Aggregates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            {[
              { l: 'Total Assets', v: fd.totalAssets, color: P.teal, sub: `Investments ${((fd.investVal / Math.max(fd.totalAssets, 1)) * 100).toFixed(0)}% · Liquid ${((fd.manualAssets / Math.max(fd.totalAssets, 1)) * 100).toFixed(0)}%` },
              { l: 'Total Liabilities', v: fd.liabilities, color: P.rose, sub: `Debt ratio ${fd.debtRatio.toFixed(1)}% · Coverage ${(fd.totalAssets / Math.max(fd.liabilities, 1)).toFixed(1)}x` },
            ].map((item, i) => (
              <div key={i} style={{ background: P.s3, border: `1px solid ${item.color}22`, borderRadius: 14, padding: '12px 14px' }}>
                <div style={{ fontSize: 9.5, color: item.color, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4, fontWeight: 700 }}>{item.l}</div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 700, marginBottom: 2 }}>{currency} {fmt(disp(item.v))}</div>
                <div style={{ fontSize: 9.5, color: P.muted }}>{item.sub}</div>
              </div>
            ))}
          </div>

          {/* Additional Health & Solvency Indicators to Fill Space */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 10 }}>
            {/* Indicator 1: Solvency / Equity Ratio */}
            <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: P.subtle, textTransform: 'uppercase', letterSpacing: '.05em' }}>Solvency Ratio</span>
                <span style={{ fontSize: 8.5, fontWeight: 700, color: P.teal }}>Optimal &gt;70%</span>
              </div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: ((fd.netWorth / Math.max(fd.totalAssets, 1)) * 100) >= 70 ? P.teal : P.gold }}>
                {((fd.netWorth / Math.max(fd.totalAssets, 1)) * 100).toFixed(1)}%
              </div>
              <div style={{ fontSize: 9, color: P.muted, marginTop: 1 }}>Equity Cushion</div>
            </div>

            {/* Indicator 2: Cash Runway */}
            <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: P.subtle, textTransform: 'uppercase', letterSpacing: '.05em' }}>Cash Runway</span>
                <span style={{ fontSize: 8.5, fontWeight: 700, color: fd.runway >= 6 ? P.teal : P.rose }}>{fd.runway >= 6 ? 'Healthy' : 'Tight'}</span>
              </div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: fd.runway >= 6 ? P.teal : P.amber }}>
                {fd.runway.toFixed(1)} <span style={{ fontSize: 11, fontWeight: 500, color: P.muted }}>Months</span>
              </div>
              <div style={{ fontSize: 9, color: P.muted, marginTop: 1 }}>{currency} {fmt(disp(fd.manualAssets))} liquid cash</div>
            </div>

            {/* Indicator 3: Capital Coverage Multiplier */}
            <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: P.subtle, textTransform: 'uppercase', letterSpacing: '.05em' }}>Asset Coverage</span>
                <span style={{ fontSize: 8.5, fontWeight: 700, color: P.sky }}>Leverage</span>
              </div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: P.text }}>
                {(fd.totalAssets / Math.max(fd.liabilities, 1)).toFixed(1)}x
              </div>
              <div style={{ fontSize: 9, color: P.muted, marginTop: 1 }}>Assets per $ debt</div>
            </div>

            {/* Indicator 4: Passive Income Run-Rate */}
            <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: P.subtle, textTransform: 'uppercase', letterSpacing: '.05em' }}>Passive Yield</span>
                <span style={{ fontSize: 8.5, fontWeight: 700, color: P.teal }}>{passiveIncomeStats.portfolioYield.toFixed(1)}%</span>
              </div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: P.teal }}>
                {currency} {fmt(disp(passiveIncomeStats.totalAnnualPassive))}<span style={{ fontSize: 10, fontWeight: 500, color: P.muted }}>/yr</span>
              </div>
              <div style={{ fontSize: 9, color: P.muted, marginTop: 1 }}>Annual dividend run-rate</div>
            </div>
          </div>

          {/* Capital Structure Micro Footnote */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: P.s3, borderRadius: 10, border: `1px solid ${P.border}` }}>
            <span style={{ fontSize: 9.5, color: P.muted }}>
              <strong style={{ color: P.text }}>Capital Structure:</strong> {((fd.investVal / Math.max(fd.totalAssets, 1)) * 100).toFixed(0)}% Invested · {((fd.manualAssets / Math.max(fd.totalAssets, 1)) * 100).toFixed(0)}% Liquid · {fd.debtRatio.toFixed(0)}% Debt
            </span>
            <button 
              onClick={() => navigate('/balance-sheet')} 
              style={{ fontSize: 9.5, fontWeight: 700, color: P.sky, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}
            >
              Audit ledger <ChevronRight size={10} />
            </button>
          </div>
        </Card>
      </div>

      {/* ══ ROW 5 — PORTFOLIO INTEL (Full Width bottom card) ══ */}
      <div style={{ background: P.s2, border: `1px solid ${P.border}`, borderRadius: 24, padding: '24px 28px', marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: `${P.gold}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.gold }}>
            <Activity size={20} />
          </div>
          <div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', color: P.text }}>Portfolio Intel & Synthesis</div>
            <div style={{ fontSize: 10, color: P.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 1 }}>Deep Analytical Intelligence engine</div>
          </div>
        </div>

        {/* Bento grid layout inside Intel */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr 0.9fr', gap: 24 }}>
          
          {/* Col 1: Executive Overview */}
          <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: P.sky, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>●</span> Executive Summary
            </div>
            
            <p style={{ fontSize: 12, color: P.text, lineHeight: 1.6, marginBottom: 14 }}>
              Your current wealth position commands {currency} {fmt(disp(fd.netWorth))} in net worth, showing a cumulative trajectory of {nwGrowth !== null ? `${nwGrowth.toFixed(1)}%` : '—'} since January baselines. 
              The financial flow is operating at a {fd.cashFlow >= 0 ? 'net positive rate' : 'net negative deficit'}, with {currency} {fmt(disp(fd.cashFlow))} active {fd.cashFlow >= 0 ? 'capital surplus' : 'erosion'}. 
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: `1px solid ${P.border}`, paddingTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: P.subtle }}>Wealth Velocity</span>
                <span style={{ fontWeight: 650, color: nwGrowth && nwGrowth >= 0 ? P.teal : P.rose }}>
                  {nwGrowth && nwGrowth >= 0 ? '+' : ''}{nwGrowth?.toFixed(1)}% / period
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: P.subtle }}>Passive Runway</span>
                <span style={{ fontWeight: 650, color: fd.runway >= 6 ? P.teal : fd.runway >= 3 ? P.gold : P.rose }}>
                  {fd.runway.toFixed(1)} months
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: P.subtle }}>Capital Surplus Margin</span>
                <span style={{ fontWeight: 650, color: fd.cashFlow >= 0 ? P.teal : P.rose }}>
                  {((fd.cashFlow / Math.max(fd.inflow, 1)) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Col 2: Actionable Directives */}
          <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: P.gold, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>●</span> Recommended Actions
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              {/* Savings action */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ marginTop: 2 }}><CheckCircle2 size={13} style={{ color: fd.savingsRate >= 30 ? P.teal : P.amber }} /></div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: P.text }}>
                    {fd.savingsRate >= 30 ? 'Maintain savings stride' : 'Optimize lifestyle outflow'}
                  </div>
                  <div style={{ fontSize: 11, color: P.subtle, marginTop: 1, lineHeight: 1.4 }}>
                    {fd.savingsRate >= 30 
                      ? 'Elite surplus rate locked. Direct excess monthly cash into higher-yielding portfolio options.' 
                      : `Savings rate is ${fd.savingsRate.toFixed(0)}%. Curb discretionary categories on actual spending to align with the 30% target.`}
                  </div>
                </div>
              </div>

              {/* Concentration action */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', borderTop: `1px solid ${P.border}`, paddingTop: 10 }}>
                <div style={{ marginTop: 2 }}>
                  <Shield size={13} style={{ color: investmentCategories.some(c => (c.currentValue / Math.max(fd.investVal, 1)) * 100 > 50) ? P.rose : P.teal }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: P.text }}>
                    {investmentCategories.some(c => (c.currentValue / Math.max(fd.investVal, 1)) * 100 > 50) ? 'Rebalance sector concentration' : 'Prudent sector weights'}
                  </div>
                  <div style={{ fontSize: 11, color: P.subtle, marginTop: 1, lineHeight: 1.4 }}>
                    {investmentCategories.some(c => (c.currentValue / Math.max(fd.investVal, 1)) * 100 > 50)
                      ? 'Single asset class holds high exposure. Reallocate capital to preserve passive gains.' 
                      : 'Sector weights show low correlation risk. Continue systematic diversified dollar-cost averaging.'}
                  </div>
                </div>
              </div>

              {/* Runway action */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', borderTop: `1px solid ${P.border}`, paddingTop: 10 }}>
                <div style={{ marginTop: 2 }}><Zap size={13} style={{ color: fd.runway < 6 ? P.rose : P.teal }} /></div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: P.text }}>
                    {fd.runway < 6 ? 'Bolster liquidity buffer' : 'Leverage surplus liquid capital'}
                  </div>
                  <div style={{ fontSize: 11, color: P.subtle, marginTop: 1, lineHeight: 1.4 }}>
                    {fd.runway < 6 
                      ? 'Runway is below safe-margin 6 months. Bolster cash balances from high yield cash reservoirs.' 
                      : 'Robust defensive runway established. Direct idle cash beyond runway to compound in diversified portfolios.'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Col 3: Financial Health Auditing Ratios */}
          <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: P.teal, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>●</span> Auditing & Ratios
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
                  <span style={{ color: P.subtle }}>Debt-to-Asset Ratio</span>
                  <span style={{ fontWeight: 650, color: fd.debtRatio < 30 ? P.teal : fd.debtRatio < 50 ? P.amber : P.rose }}>{fd.debtRatio.toFixed(1)}%</span>
                </div>
                <div style={{ height: 6, background: P.s1, borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(fd.debtRatio, 100)}%`, background: fd.debtRatio < 30 ? P.teal : fd.debtRatio < 50 ? P.gold : P.rose, borderRadius: 99 }} />
                </div>
                <div style={{ fontSize: 9, color: P.muted, marginTop: 3 }}>Healthy: &lt;30% · Leverage risk starts above 50%</div>
              </div>

              <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
                  <span style={{ color: P.subtle }}>Expense Cushion</span>
                  <span style={{ fontWeight: 650, color: fd.runway >= 6 ? P.teal : P.rose }}>{fd.runway.toFixed(1)}x</span>
                </div>
                <div style={{ height: 6, background: P.s1, borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min((fd.runway / 12) * 100, 100)}%`, background: fd.runway >= 6 ? P.teal : P.rose, borderRadius: 99 }} />
                </div>
                <div style={{ fontSize: 9, color: P.muted, marginTop: 3 }}>Target: 6.0x monthly outflow reserves</div>
              </div>

              <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
                  <span style={{ color: P.subtle }}>Active Surplus Rate</span>
                  <span style={{ fontWeight: 650, color: fd.cashFlow >= 0 ? P.teal : P.rose }}>
                    {fd.inflow > 0 ? ((fd.cashFlow / fd.inflow) * 100).toFixed(0) : 0}%
                  </span>
                </div>
                <div style={{ height: 6, background: P.s1, borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(Math.max((fd.cashFlow / Math.max(fd.inflow, 1)) * 100, 0), 100)}%`, background: P.teal, borderRadius: 99 }} />
                </div>
                <div style={{ fontSize: 9, color: P.muted, marginTop: 3 }}>Net savings surplus divided by inputs</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
